/** Odds (in %) per rarity, keyed 0..3 (Rookie / Semi-Pro / Pro / Legendary). */
export type CardRarityOdds = Record<string, number | undefined>;

export const RARITY_LABELS: Record<string, string> = {
  '0': 'Rookie',
  '1': 'Semi-Pro',
  '2': 'Pro',
  '3': 'Legendary',
};

const RARITY_KEYS = ['0', '1', '2', '3'];
// Same tolerance as the server: 33.33 + 33.33 + 33.34 is not exactly 100 in floating point.
const SUM_TOLERANCE = 0.001;

export function rarityOddsTotal(odds: CardRarityOdds): number {
  const sum = RARITY_KEYS.reduce((s, k) => s + (odds[k] ?? 0), 0);
  return Math.round(sum * 100) / 100;
}

/** The first thing stopping a save, in plain words, or null when the server will accept the odds. */
export function rarityOddsProblem(odds: CardRarityOdds): string | null {
  for (const k of RARITY_KEYS) {
    const v = odds[k];
    if (typeof v !== 'number' || !Number.isFinite(v) || v < 0 || v > 100) {
      return `${RARITY_LABELS[k]} needs a value from 0 to 100.`;
    }
  }
  const total = RARITY_KEYS.reduce((s, k) => s + (odds[k] as number), 0);
  if (Math.abs(total - 100) > SUM_TOLERANCE) {
    return `The odds add up to ${rarityOddsTotal(odds)}%; they must add up to 100%.`;
  }
  return null;
}

/** A percentage as "1 in N cards", so 3% reads as roughly one card in 33. */
export function oneInN(percent: number): string {
  if (percent <= 0) return 'never';
  if (percent >= 100) return 'every card';
  const n = 100 / percent;
  const rounded = Math.round(n);
  return Math.abs(n - rounded) < 0.01 ? `1 in ${rounded} cards` : `about 1 in ${rounded} cards`;
}
